// =============================================================================
// runLots — the shapes behind the run's Lots section, and the pure maths that
// turns the three lot views into one grouped table.
//
// A LOT is a tipping_log row (mig 20261019100000). The views group the tips
// of one SOURCE under a lot_key — 'pallet:<id>' for a bin stripped off a
// received pallet, else 'bin:' / 'batch:' / 'delivery:' — and answer per lot
// (run_lot_packout), per lot per SAST day (run_lot_packout_daily) and per lot
// per day per product (run_lot_products). This module joins those three
// client-side into lot → days → products, and labels them the way an operator
// names things: the sender's pallet ref, the DN number, the batch number.
//
// "Rejected cartons" is a carton-EQUIVALENT (reject kg ÷ the lot's own kg per
// carton). Tipped fruit leaves its cartons behind; nobody can count a rejected
// carton, so the number is shown with ≈ and never without the kg beside it.
// =============================================================================
import { toCSV } from '@/lib/csv'
import { readState, staleness, type ReadStateLike } from '@/lib/readState'

export const AUTO_LOT = 'auto'

export type LotSourceKind = 'BIN' | 'BATCH' | 'DELIVERY'

export interface LotPackoutRow {
  run_id:               string
  lot_key:              string
  source_kind:          LotSourceKind
  source_pallet_id:     string | null
  source_pallet_ref:    string | null
  bin_number:           string | null
  delivery_id:          string | null
  client_ref_no:        string | null
  delivery_note_number: string | null
  batch_number:         string | null
  first_tipped_at:      string
  last_tipped_at:       string
  tip_count:            number
  bins_in:              number
  kg_in:                number
  cartons_in:           number | null
  cartons_out:          number
  kg_out:               number
  reject_kg:            number
  waste_kg:             number
  packout_pct:          number | null
  unexplained_kg:       number
  kg_per_carton_in:     number | null
  reject_ctn_equiv:     number | null
  waste_ctn_equiv:      number | null
  /** Cartons condemned unopened, counted beside the kg (mig 20261019110000). */
  whole_cartons:        number
}

export interface LotDayRow {
  run_id:           string
  lot_key:          string
  day:              string          // 'YYYY-MM-DD', SAST calendar day
  cartons_tipped:   number
  kg_tipped:        number
  cartons_packed:   number
  kg_packed:        number
  reject_kg:        number
  waste_kg:         number
  reject_ctn_equiv: number | null
  waste_ctn_equiv:  number | null
  whole_cartons:    number
}

export interface LotProductRow {
  run_id:          string
  lot_key:         string
  day:             string
  product_id:      string | null
  product_name:    string | null
  product_variant: string | null
  product_code:    string | null
  pack_type:       string | null
  grade:           string | null
  size_code:       string | null
  cartons:         number
  kg:              number
  pallets:         number
}

export interface LotUnassignedRow {
  run_id:      string
  cartons_out: number
  kg_out:      number
  reject_kg:   number
  waste_kg:    number
  whole_cartons: number
}

export interface LotDay extends LotDayRow { products: LotProductRow[] }
export interface LotGroup { lot: LotPackoutRow; days: LotDay[] }

/** The operator's name for a lot: the received pallet (+ sender ref), else the bin, batch or delivery. */
export function lotLabel(l: Pick<LotPackoutRow, 'source_kind' | 'source_pallet_id' | 'source_pallet_ref' | 'bin_number' | 'client_ref_no' | 'delivery_note_number' | 'batch_number'>): string {
  if (l.source_pallet_id) return l.source_pallet_ref ? `${l.source_pallet_id} · ref ${l.source_pallet_ref}` : l.source_pallet_id
  if (l.source_kind === 'BIN')   return l.bin_number ? `Bin ${l.bin_number}` : 'Stored bin'
  if (l.source_kind === 'BATCH') return l.batch_number ? `Batch ${l.batch_number}` : 'Cooling batch'
  if (l.delivery_note_number)    return `DN ${l.delivery_note_number}`
  if (l.client_ref_no)           return `Ref ${l.client_ref_no}`
  return 'Delivery'
}

/** What was packed: the rate-card product when the line is coded, else pack type · class · size. */
export function productLabel(p: Pick<LotProductRow, 'product_name' | 'product_variant' | 'product_code' | 'pack_type' | 'grade' | 'size_code'>): string {
  const coded = [p.product_name?.trim(), p.product_variant?.trim()].filter(Boolean).join(' · ')
  if (coded) return coded
  const bits = [p.pack_type?.trim(), p.grade ? `Cl ${p.grade}` : null, p.size_code?.trim()].filter(Boolean)
  return bits.length ? bits.join(' · ') : (p.product_code?.trim() || 'Uncoded')
}

/** lot → days (ascending) → products, lots in tip order. Rows of unknown lots are dropped, not invented. */
export function groupLots(lots: LotPackoutRow[], days: LotDayRow[], products: LotProductRow[]): LotGroup[] {
  const byLot = new Map<string, LotGroup>()
  for (const lot of [...lots].sort((a, b) => a.first_tipped_at.localeCompare(b.first_tipped_at))) {
    byLot.set(lot.lot_key, { lot, days: [] })
  }
  const dayIndex = new Map<string, LotDay>()
  for (const d of [...days].sort((a, b) => a.day.localeCompare(b.day))) {
    const g = byLot.get(d.lot_key)
    if (!g) continue
    const row: LotDay = { ...d, products: [] }
    g.days.push(row)
    dayIndex.set(`${d.lot_key}|${d.day}`, row)
  }
  for (const p of products) {
    dayIndex.get(`${p.lot_key}|${p.day}`)?.products.push(p)
  }
  for (const row of dayIndex.values()) {
    row.products.sort((a, b) => b.cartons - a.cartons || productLabel(a).localeCompare(productLabel(b)))
  }
  return [...byLot.values()]
}

/** True when any output or reject on the run carries no lot — the Unassigned row earns its place. */
export function hasUnassigned(u: LotUnassignedRow | null | undefined): boolean {
  return !!u && (u.cartons_out > 0 || u.kg_out > 0 || u.reject_kg > 0 || u.waste_kg > 0)
}

const n1 = (v: number | null | undefined) => v == null ? '' : Number(v).toFixed(1)
const n0 = (v: number | null | undefined) => v == null ? '' : String(Math.round(Number(v)))

/** One CSV: a lot row, then its day rows, then each day's product rows. */
export function lotsCsv(runNumber: string, groups: LotGroup[], unassigned: LotUnassignedRow | null): string {
  type R = unknown[]
  const rows: R[] = []
  for (const g of groups) {
    const l = g.lot
    rows.push(['lot', lotLabel(l), l.source_kind, '', n0(l.cartons_in), n1(l.kg_in), n0(l.cartons_out), n1(l.kg_out),
               n1(l.reject_kg), n1(l.reject_ctn_equiv), n0(l.whole_cartons), n1(l.waste_kg), n1(l.packout_pct), n1(l.unexplained_kg), ''])
    for (const d of g.days) {
      rows.push(['day', lotLabel(l), l.source_kind, d.day, n0(d.cartons_tipped), n1(d.kg_tipped), n0(d.cartons_packed), n1(d.kg_packed),
                 n1(d.reject_kg), n1(d.reject_ctn_equiv), n0(d.whole_cartons), n1(d.waste_kg), '', '', ''])
      for (const p of d.products) {
        rows.push(['product', lotLabel(l), l.source_kind, d.day, '', '', n0(p.cartons), n1(p.kg), '', '', '', '', '', '', productLabel(p)])
      }
    }
  }
  if (hasUnassigned(unassigned) && unassigned) {
    rows.push(['unassigned', 'No lot', '', '', '', '', n0(unassigned.cartons_out), n1(unassigned.kg_out),
               n1(unassigned.reject_kg), '', n0(unassigned.whole_cartons), n1(unassigned.waste_kg), '', '', ''])
  }
  return toCSV(
    ['row', 'lot', 'source', 'day', 'ctns_in', 'kg_in', 'ctns_out', 'kg_out', 'reject_kg', 'reject_ctn_equiv',
     'whole_cartons', 'waste_kg', 'packout_pct', 'unexplained_kg', 'product'],
    rows.map(r => [runNumber, ...r]).map(r => r), r => r,
  ).replace(/^row,/, 'run,row,')
}

/** A stable React key for a product chip — the coded product, else its describing facts. */
export function productKey(p: Pick<LotProductRow, 'product_id' | 'pack_type' | 'grade' | 'size_code'>): string {
  return [p.product_id, p.pack_type, p.grade, p.size_code].map(x => x ?? '').join('|')
}

/** The grey subtitle after a lot's name: its kind, and how many tips make it up. */
export function lotSubtitle(l: Pick<LotPackoutRow, 'source_kind' | 'tip_count'>): string {
  const kind = l.source_kind === 'BIN' ? 'stored bin' : l.source_kind.toLowerCase()
  return l.tip_count > 1 ? `${kind} · ${l.tip_count} tips` : kind
}

export interface LotChoice<T extends { tipping_id: string; label: string }> {
  /** AUTO_LOT, or the tipping_id the operator picked (only while that tip still exists). */
  choice:             string
  isAuto:             boolean
  effective:          T | null
  effectiveTippingId: string | null
  effectiveLabel:     string | null
}

/**
 * Which lot a capture writes: the operator's remembered pick while its tip
 * still exists on the run, else the run's LATEST tip (tips arrive newest
 * first), else nothing — a run with no tips writes NULL (unassigned).
 */
export function resolveLot<T extends { tipping_id: string; label: string }>(tips: T[], choice: string): LotChoice<T> {
  const chosen = choice === AUTO_LOT ? null : (tips.find(t => t.tipping_id === choice) ?? null)
  const effective = chosen ?? tips[0] ?? null
  return {
    choice:             chosen ? choice : AUTO_LOT,
    isAuto:             chosen === null,
    effective,
    effectiveTippingId: effective ? effective.tipping_id : null,
    effectiveLabel:     effective ? effective.label : null,
  }
}

export interface RunLotsData { groups: LotGroup[]; unassigned: LotUnassignedRow | null }
export const EMPTY_RUN_LOTS: RunLotsData = { groups: [], unassigned: null }

/** The four view reads → one grouped shape. Missing data reads as empty, never as invented rows. */
export function buildRunLotsData(lots: unknown, days: unknown, products: unknown, unassigned: unknown): RunLotsData {
  const arr = <T,>(x: unknown): T[] => (Array.isArray(x) ? x : []) as T[]
  return {
    groups: groupLots(arr<LotPackoutRow>(lots), arr<LotDayRow>(days), arr<LotProductRow>(products)),
    unassigned: (unassigned ?? null) as LotUnassignedRow | null,
  }
}

/** "KF49-2026-00811 · ref SW-4471" — how a source pallet is named on documents. */
export function sourcePalletLabel(s: { source_pallet_id: string | null; source_pallet_ref: string | null }): string | null {
  if (!s.source_pallet_id) return null
  return s.source_pallet_ref ? `${s.source_pallet_id} · ref ${s.source_pallet_ref}` : s.source_pallet_id
}

/** line_id → source-pallet label, for lines whose lot is a received pallet. */
export function mapLineSources(
  lines:   { line_id: string; source_tipping_id: string | null }[],
  sources: { tipping_id: string; source_pallet_id: string | null; source_pallet_ref: string | null }[],
): Record<string, string> {
  const byTip = new Map<string, string>()
  for (const s of sources) {
    const label = sourcePalletLabel(s)
    if (label) byTip.set(s.tipping_id, label)
  }
  const out: Record<string, string> = {}
  for (const l of lines) {
    const label = l.source_tipping_id ? byTip.get(l.source_tipping_id) : undefined
    if (label) out[l.line_id] = label
  }
  return out
}

/** "P1 · ref SW-1 · tipped 03 Sep, 08:00" — a tip as a lot picker lists it. */
export function tipOptionLabel(t: { label: string; tipped_at: string }): string {
  const at = new Date(t.tipped_at).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  return `${t.label} · tipped ${at}`
}

/**
 * The lot fragment of one build-up line's save payload — spread it in.
 *
 * `undefined` means the dialog never learnt this line's lot (a line read off a
 * cache from before the column was selected), so the key is left OUT and
 * save_pallet_build_up keeps what the row has: absent is "no opinion", not
 * NULL (mig 20261025200000). '' is a decision — no lot — and sends null.
 */
export function lineLotPatch(lot: string | undefined): { source_tipping_id?: string | null } {
  return lot === undefined ? {} : { source_tipping_id: lot || null }
}

/** A read line's lot as the dialog holds it: '' for none, undefined when the read never carried the field. */
export function lotDraftValue(lot: string | null | undefined): string | undefined {
  return lot === undefined ? undefined : (lot ?? '')
}

/** One `run_lot_sources` row, as far as naming its lot needs. */
export type LotSourceRow = Pick<LotPackoutRow, 'source_pallet_id' | 'source_pallet_ref' | 'bin_number' | 'client_ref_no' | 'delivery_note_number' | 'batch_number'>
  & { tipping_id: string | null; source_kind: string | null }

/** tipping_id → the operator's name for that lot. A row with no tip id names nothing. */
export function lotLabelsById(rows: LotSourceRow[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const r of rows.filter(r => r.tipping_id)) {
    out[r.tipping_id as string] = lotLabel({ ...r, source_kind: r.source_kind as LotSourceKind })
  }
  return out
}

export interface LotPickerState {
  /** Nothing to choose: the run has no tips (a read that SUCCEEDED said so, or
   *  it is still in flight) and the line names no lot. Never on a failed read —
   *  hiding the picker then would claim the run has no lots. */
  hidden:      boolean
  /** The select's value — '' is "no lot". */
  current:     string
  /** Text for a lot the run's tips don't list, else null: still loading, the
   *  read failed (so it can't be named), or gone — the last only when the
   *  latest read succeeded and does not list it. */
  orphanLabel: string | null
  /** The run's tips could not be read and it matters on this line: 'unread' =
   *  no list at all, 'stale' = an older list that can't name the line's lot.
   *  Offer a Retry. null = nothing to say. */
  retry:       'unread' | 'stale' | null
}

export const LOT_PICKER_TEXT = {
  loading: 'Loading this run’s lots…',
  unread:  'This line’s lot — couldn’t load this run’s lots to name it',
  gone:    'A lot that is no longer on this run',
} as const

/**
 * What a build-up line's Lot picker shows, given the read of its run's tips
 * and the line's lot. Display only: the select keeps `current` whatever the
 * read did, so a save sends the line's lot exactly as before.
 *
 * "No longer on this run" is a claim about the run, so it is made only off a
 * read that SUCCEEDED. A failed first read holds no list; a failed REFETCH
 * keeps the old one (react-query v5: status 'error' with data still there —
 * see readState.ts), and a lot tipped since that list was read is missing
 * from it without being gone.
 */
export function lotPickerState(
  tipsQ: ReadStateLike & { data?: { tipping_id: string }[] },
  value: string | undefined,
): LotPickerState {
  const read    = readState(tipsQ)
  const settled = read === 'usable' && staleness(tipsQ) === null
  const tips    = tipsQ.data ?? []
  const current = value ?? ''
  const listed  = current === '' || tips.some(t => t.tipping_id === current)
  return {
    hidden:      current === '' && (read === 'loading' || (settled && tips.length === 0)),
    current,
    orphanLabel: listed ? null : orphanText(read, settled),
    retry:       retryFor(read, settled, !listed || tips.length === 0),
  }
}

/** The text for a lot the list doesn't name: "gone" only off a read that succeeded. */
function orphanText(read: ReturnType<typeof readState>, settled: boolean): string {
  if (read === 'loading') return LOT_PICKER_TEXT.loading
  return settled ? LOT_PICKER_TEXT.gone : LOT_PICKER_TEXT.unread
}

/** A Retry where the read failed and it matters. An older list only needs one
 *  where it is making a claim: it can't name the line's lot, or it is empty
 *  (which would otherwise hide the picker). */
function retryFor(read: ReturnType<typeof readState>, settled: boolean, listClaims: boolean): LotPickerState['retry'] {
  if (read === 'unread') return 'unread'
  return read === 'usable' && !settled && listClaims ? 'stale' : null
}

/**
 * "From 2 lots — 30 × P1 · ref SW-1, 20 × P2." for a pallet whose cartons came
 * off more than one lot (two received pallets packed onto one), else null.
 * Lines are grouped by the lot's NAME, so one received pallet stripped in two
 * tips is still one source. Null while any named lot has no label yet: the
 * sentence is never built from a guess.
 */
export function lineLotsNote(
  lines:  { carton_count: number; source_tipping_id?: string | null }[],
  labels: Record<string, string>,
): string | null {
  const byName = new Map<string, number>()
  for (const l of lines) {
    const id = l.source_tipping_id ?? null
    const name = id === null ? 'no lot' : labels[id]
    if (name === undefined) return null
    byName.set(name, (byName.get(name) ?? 0) + l.carton_count)
  }
  if (byName.size < 2) return null
  const parts = [...byName.entries()].sort((a, b) => b[1] - a[1]).map(([n, c]) => `${c} × ${n}`)
  return `From ${byName.size} lots — ${parts.join(', ')}.`
}

export interface PrepackedStaged { pallets: number; cartons: number; receipts: number; title: string; sub: string }

/** The "Prepacked · N pallets staged" card's figures and copy, or null when nothing is staged. */
export function prepackedStagedSummary(rows: { carton_count: number | null; run_id: string }[]): PrepackedStaged | null {
  if (rows.length === 0) return null
  const cartons  = rows.reduce((s, r) => s + (r.carton_count ?? 0), 0)
  const receipts = new Set(rows.map(r => r.run_id)).size
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`
  return {
    pallets: rows.length, cartons, receipts,
    title: `Prepacked · ${plural(rows.length, 'pallet')} staged`,
    sub:   `${cartons} ctns on ${plural(receipts, 'receipt')} · add them to a run or create one from the receipt`,
  }
}

// ── A new pallet packed from more than one lot (mig 20261031990000) ─────────
// The New Pallet form's "From more than one pallet" rows. Saved as
// pallets.lot_split; the auto-line trigger makes one line per row and refuses
// the same problems this names, so the form says them first.

export interface LotSplitRow { tipping_id: string; cartons: string }

/** Two rows: the lot the form was already on, and one to pick. */
export function startLotSplit(firstLot: string | null): LotSplitRow[] {
  return [{ tipping_id: firstLot ?? '', cartons: '' }, { tipping_id: '', cartons: '' }]
}

const rowCartons = (r: LotSplitRow) => parseInt(r.cartons, 10) || 0

export function lotSplitTotal(rows: LotSplitRow[]): number {
  return rows.reduce((s, r) => s + rowCartons(r), 0)
}

/** What stops the split being saved, in the operator's words — or null. */
export function lotSplitProblem(rows: LotSplitRow[], cartonQty: number): string | null {
  if (rows.length < 2) return 'Add a second pallet, or untick “From more than one pallet”.'
  if (rows.some(r => !r.tipping_id)) return 'Pick the pallet for every row.'
  if (new Set(rows.map(r => r.tipping_id)).size < rows.length) return 'The same pallet is picked twice.'
  if (rows.some(r => rowCartons(r) <= 0)) return 'Enter the cartons from every pallet.'
  const total = lotSplitTotal(rows)
  return total === cartonQty ? null : `The pallets’ cartons add up to ${total}, not the pallet’s ${cartonQty}.`
}

/** "22 + 38 = 60 of 60 ctns" */
export function lotSplitSummary(rows: LotSplitRow[], cartonQty: number): string {
  return `${rows.map(rowCartons).join(' + ')} = ${lotSplitTotal(rows)} of ${cartonQty} ctns`
}

/** pallets.lot_split as the database reads it. */
export function lotSplitPayload(rows: LotSplitRow[]): { source_tipping_id: string; carton_count: number }[] {
  return rows.map(r => ({ source_tipping_id: r.tipping_id, carton_count: rowCartons(r) }))
}
